import { useMemo } from 'react';
import { ACCOUNTS_ROLES } from '../../../constants/roles';
import { useDebounce } from '../../../hooks/useDebounce';
import { useGetOmniSearchList } from '../../../hooks/useGetOmniSearchList';
import { useAuth } from '../../../services/auth0';
import { isEntityIdQuery } from '../../../utils/recordIds';
import { CommandPaletteItem } from './commandPalette.types';
import {
  buildAccountSearchFallbackItem,
  mapRecordsToCommandItems,
  MIN_RECORD_SEARCH_LENGTH,
} from './commandPaletteRecords.utils';

interface UseCommandPaletteRecordItemsProps {
  query: string;
  isOpen: boolean;
}

interface UseCommandPaletteRecordItemsResult {
  recordItems: CommandPaletteItem[];
  /** True while the debounce window is pending or a fetch is in flight. */
  isSearching: boolean;
  /** True when the (debounced) query is a record ID like `quot_abc123`. */
  isIdQuery: boolean;
  /** Section header for the record results, e.g. "Accounts" or "Quotes". */
  sectionLabel: string;
}

/**
 * Searches records for the command palette: queries that look like record IDs
 * fetch that record directly, anything else searches accounts by name — the
 * same behavior as the sidebar account search (useGetOmniSearchList).
 */
export const useCommandPaletteRecordItems = ({
  query,
  isOpen,
}: UseCommandPaletteRecordItemsProps): UseCommandPaletteRecordItemsResult => {
  const { currentTenantUserHasRole } = useAuth();
  const trimmed = query.trim();
  const debounced = useDebounce(trimmed);

  // Three-part gating:
  // - isOpen: the palette stays mounted when closed, and the by-ID branch of
  //   useGetOmniSearchList is not gated on isOpen — an empty value disables it
  // - trimmed length: on reopen the query resets to '' but the debounced value
  //   holds the stale query for the debounce window; requiring the *current*
  //   value prevents refetching the previous search
  // - debounced length: don't search until the user has typed enough
  const searchTerm =
    isOpen &&
    trimmed.length >= MIN_RECORD_SEARCH_LENGTH &&
    debounced.length >= MIN_RECORD_SEARCH_LENGTH
      ? debounced
      : '';

  const { data, isFetching, fieldConfig } = useGetOmniSearchList({
    debouncedValue: searchTerm,
    apiKey: 'accountSearch',
    sortField: 'accountName',
    isOpen,
  });
  const { nameField, entityDisplayName } = fieldConfig;

  const isIdQuery = isEntityIdQuery(searchTerm);
  const canSearchAccounts = currentTenantUserHasRole(ACCOUNTS_ROLES);
  const records = data?.content;

  const recordItems = useMemo(() => {
    if (!searchTerm) {
      return [];
    }
    const items = mapRecordsToCommandItems(records ?? [], {
      nameField,
      entityDisplayName,
    });
    if (!isIdQuery && canSearchAccounts) {
      items.push(buildAccountSearchFallbackItem(searchTerm));
    }
    return items;
  }, [
    searchTerm,
    records,
    nameField,
    entityDisplayName,
    isIdQuery,
    canSearchAccounts,
  ]);

  const isSearching =
    isOpen &&
    trimmed.length >= MIN_RECORD_SEARCH_LENGTH &&
    (trimmed !== debounced || isFetching);

  return {
    recordItems,
    isSearching,
    isIdQuery,
    sectionLabel: entityDisplayName,
  };
};
